// Copyright © SRGTech (Software Resonance Group Inc.). All rights reserved.
// SPDX-License-Identifier: LicenseRef-SRGTech-Proprietary
// See LICENSE and docs/legal/PROPRIETARY_COPYRIGHT.md.

/**
 * Registering a college (**ADR-0067**, **ADR-0075**).
 *
 * Three fields, matching `TenantRegistration` exactly: name, code, and an
 * optional address. There is deliberately no field for a database host, a
 * connector credential, a Kubernetes namespace, an image, a ServiceAccount, or
 * any runtime setting — none of those is part of registration, and the BFF
 * rejects an unknown field outright (`SEC-006`, `CON-003`).
 *
 * Client validation is an affordance, never the decision. `CST-048` keeps the
 * decision on the server, so every field is also bounded upstream and a `422`
 * is rendered as field-level guidance rather than being treated as impossible.
 *
 * Focus is moved deliberately. On failure it goes to the first field in error.
 * On success it does **not** stay here: registration now discloses the
 * college's first activation code (**ADR-0076** §5), so the caller opens the
 * one-time disclosure and focus enters that dialog. This panel hands the result
 * and its own submit button upwards — the button is where focus must return
 * when the disclosure closes, and only this component knows which one it is
 * (`WCAG 2.2 AA`, `docs/09-UX.md`).
 */

import { ApiProblemError, useApiClients, type TenantCreateResult } from '@eai/ui-shared';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useCallback, useRef, useState, type FormEvent, type ReactNode } from 'react';

/** Field bounds, mirroring `TenantRegistration`. */
export const MAX_NAME_LENGTH = 200;
export const MAX_CODE_LENGTH = 64;
export const MAX_ADDRESS_LENGTH = 500;

/** Props for {@link AddCollegePanel}. */
export interface AddCollegePanelProps {
  /**
   * Called after a confirmed registration.
   *
   * @param result - What the control plane recorded, including the one-time
   *   activation issuance. The caller discloses it once and discards it.
   * @param initiator - This panel's submit button, so the caller can return
   *   focus to it when the disclosure closes.
   */
  readonly onRegistered: (
    result: TenantCreateResult,
    initiator: HTMLElement | null,
  ) => Promise<void>;
}

interface FieldErrors {
  readonly name?: string;
  readonly code?: string;
  readonly address?: string;
}

/** Validate what can be checked here. The server checks all of it again. */
export function validate(name: string, code: string, address: string): FieldErrors {
  const errors: { name?: string; code?: string; address?: string } = {};
  if (name.trim().length === 0) {
    errors.name = 'Enter the college name.';
  } else if (name.length > MAX_NAME_LENGTH) {
    errors.name = `The college name may be at most ${String(MAX_NAME_LENGTH)} characters.`;
  }
  if (code.trim().length === 0) {
    errors.code = 'Enter the college code.';
  } else if (code.length > MAX_CODE_LENGTH) {
    errors.code = `The college code may be at most ${String(MAX_CODE_LENGTH)} characters.`;
  }
  if (address.length > MAX_ADDRESS_LENGTH) {
    errors.address = `The address may be at most ${String(MAX_ADDRESS_LENGTH)} characters.`;
  }
  return errors;
}

/**
 * Translate a refusal into something an operator can act on.
 *
 * Upstream text is never shown: `API-012` keeps it out of the browser, and a
 * database constraint name tells an administrator nothing useful anyway.
 */
export function describeFailure(error: unknown): string {
  if (!(error instanceof ApiProblemError)) {
    return 'The college could not be registered. Try again.';
  }
  switch (error.status) {
    case 409:
      return 'A college with this code is already registered. Use a different code.';
    case 422:
      return 'Check the highlighted fields and try again.';
    case 401:
      return 'Your session has ended. Sign in again to continue.';
    case 403:
      return 'Your account is not permitted to register a college.';
    case 503:
    case 504:
      return 'The service is unavailable. Try again shortly.';
    default:
      return 'The college could not be registered. Try again.';
  }
}

/** Register a college. */
export function AddCollegePanel({ onRegistered }: AddCollegePanelProps): ReactNode {
  const clients = useApiClients();
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [address, setAddress] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [failure, setFailure] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const submitRef = useRef<HTMLButtonElement | null>(null);
  // The in-flight flag is mirrored in a ref because the guard has to hold
  // within one event-loop turn: two submissions dispatched before React
  // re-renders would both read the same `submitting` state and both register
  // the college.
  const submittingRef = useRef(false);
  const nameRef = useRef<HTMLInputElement | null>(null);
  const codeRef = useRef<HTMLInputElement | null>(null);

  const focusFirstError = useCallback((found: FieldErrors): void => {
    if (found.name !== undefined) {
      nameRef.current?.focus();
      return;
    }
    if (found.code !== undefined) {
      codeRef.current?.focus();
    }
  }, []);

  const submit = useCallback(
    async (event: FormEvent<HTMLFormElement>): Promise<void> => {
      event.preventDefault();
      // A second submission while the first is in flight would register the
      // college twice. The button is disabled, but a keyboard `Enter` on a
      // field reaches this handler without touching it, so the guard is here
      // rather than only in the markup.
      if (submittingRef.current) {
        return;
      }
      setFailure(null);
      setConfirmation(null);

      const found = validate(name, code, address);
      setErrors(found);
      if (Object.keys(found).length > 0) {
        focusFirstError(found);
        return;
      }

      submittingRef.current = true;
      setSubmitting(true);
      try {
        const created = await clients.controlPlane.registerCollege({
          name: name.trim(),
          code: code.trim(),
          ...(address.trim().length > 0 ? { address: address.trim() } : {}),
        });
        // The confirmation names what the server recorded, not what was typed:
        // the two differ if the control plane normalised anything.
        setConfirmation(
          `${created.name} (${created.code}) is registered and pending onboarding. ` +
            'EAI Edge provisioning has not started.',
        );
        setName('');
        setCode('');
        setAddress('');
        setErrors({});
        // The issuance travels no further than this call. It is handed to the
        // caller, which holds it in state for one disclosure and discards it;
        // nothing about it is kept here.
        await onRegistered(created, submitRef.current);
      } catch (error) {
        setFailure(describeFailure(error));
        if (error instanceof ApiProblemError && error.status === 422) {
          // The server found something this form did not. Its field pointers are
          // not echoed — they name upstream paths — so the fields are marked
          // generically and focus moves to the first one.
          const serverErrors: FieldErrors = {
            name: 'Check this value.',
            code: 'Check this value.',
          };
          setErrors(serverErrors);
          focusFirstError(serverErrors);
        }
      } finally {
        submittingRef.current = false;
        setSubmitting(false);
      }
    },
    [address, clients, code, focusFirstError, name, onRegistered],
  );

  return (
    <Paper
      variant="outlined"
      sx={{ p: 2, mb: 2 }}
      component="section"
      aria-labelledby="add-college"
    >
      <Typography id="add-college" variant="h2" sx={{ fontSize: '1.125rem', mb: 1 }}>
        Add College
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Registration records the college&rsquo;s name, code, and address. It does not collect
        database, directory, or connector credentials, and it does not provision an EAI Edge
        appliance.
      </Typography>

      {confirmation === null ? null : (
        // Announced through a live region rather than by moving focus. Focus
        // goes into the one-time disclosure that opens on success, and two
        // components competing for it is how an operator loses the code.
        <div data-testid="add-college-confirmation">
          <Alert severity="success" sx={{ mb: 2 }} role="status">
            {confirmation}
          </Alert>
        </div>
      )}
      {failure === null ? null : (
        <Alert severity="error" sx={{ mb: 2 }} role="alert" data-testid="add-college-error">
          {failure}
        </Alert>
      )}

      <Box component="form" onSubmit={(event) => void submit(event)} noValidate>
        <Stack spacing={2}>
          <TextField
            label="College name"
            value={name}
            onChange={(event) => {
              setName(event.target.value);
            }}
            required
            inputRef={nameRef}
            error={errors.name !== undefined}
            helperText={errors.name ?? ' '}
            slotProps={{ htmlInput: { maxLength: MAX_NAME_LENGTH, 'aria-describedby': undefined } }}
          />
          <TextField
            label="College code"
            value={code}
            onChange={(event) => {
              setCode(event.target.value);
            }}
            required
            inputRef={codeRef}
            error={errors.code !== undefined}
            helperText={errors.code ?? 'A short unique identifier, for example EX-01.'}
            slotProps={{ htmlInput: { maxLength: MAX_CODE_LENGTH } }}
          />
          <TextField
            label="Address (optional)"
            value={address}
            onChange={(event) => {
              setAddress(event.target.value);
            }}
            error={errors.address !== undefined}
            helperText={errors.address ?? ' '}
            slotProps={{ htmlInput: { maxLength: MAX_ADDRESS_LENGTH } }}
          />
          <Box>
            <Button type="submit" variant="contained" disabled={submitting} ref={submitRef}>
              {submitting ? 'Registering…' : 'Register college'}
            </Button>
          </Box>
        </Stack>
      </Box>
    </Paper>
  );
}
